import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Webhook, Plus, Send, Trash2, X,
  CheckCircle, AlertCircle, Clock,
} from 'lucide-react'
import clsx from 'clsx'
import { Card, Badge, PageHeader, EmptyState } from '../components/Card'
import ErrorBoundary from '../components/ErrorBoundary'
import { useToast } from '../components/Toast'
import { layout } from '../utils/layout'
import {
  listWebhookEndpoints, createWebhookEndpoint, deleteWebhookEndpoint,
  pingWebhookEndpoint, webhookDeliveries,
  type WebhookEndpointItem, type WebhookDeliveryItem,
} from '../api/client'

// ── Helpers ──────────────────────────────────────────────────────────────────

function deriveHealth(failureCount: number): WebhookHealth {
  if (failureCount === 0) return 'healthy'
  if (failureCount < 10) return 'degraded'
  return 'failing'
}

// ── Types ────────────────────────────────────────────────────────────────────

type WebhookHealth = 'healthy' | 'degraded' | 'failing'

// ── Constants ────────────────────────────────────────────────────────────────

const EVENT_TYPES = [
  { key: 'content.created', label: 'Content Created', group: 'Content' },
  { key: 'content.published', label: 'Content Published', group: 'Content' },
  { key: 'content.approved', label: 'Content Approved', group: 'Content' },
  { key: 'campaign.created', label: 'Campaign Created', group: 'Ads' },
  { key: 'campaign.optimized', label: 'Campaign Optimized', group: 'Ads' },
  { key: 'campaign.paused', label: 'Campaign Paused', group: 'Ads' },
  { key: 'crisis.detected', label: 'Crisis Detected', group: 'Crisis' },
  { key: 'crisis.escalated', label: 'Crisis Escalated', group: 'Crisis' },
  { key: 'crisis.resolved', label: 'Crisis Resolved', group: 'Crisis' },
  { key: 'inbox.new_thread', label: 'New Thread', group: 'Inbox' },
  { key: 'inbox.assigned', label: 'Thread Assigned', group: 'Inbox' },
  { key: 'experiment.concluded', label: 'Experiment Concluded', group: 'Experiments' },
  { key: 'agent.completed', label: 'Agent Task Completed', group: 'Agents' },
  { key: 'agent.failed', label: 'Agent Task Failed', group: 'Agents' },
]

const HEALTH_CONFIG: Record<WebhookHealth, { color: string; bgColor: string; label: string; badgeColor: 'green' | 'yellow' | 'red' }> = {
  healthy:  { color: 'text-green-400',  bgColor: 'bg-green-400',  label: 'Healthy',  badgeColor: 'green'  },
  degraded: { color: 'text-yellow-400', bgColor: 'bg-yellow-400', label: 'Degraded', badgeColor: 'yellow' },
  failing:  { color: 'text-red-400',    bgColor: 'bg-red-400',    label: 'Failing',  badgeColor: 'red'    },
}

// ── Loading Skeleton ─────────────────────────────────────────────────────────

function WebhooksSkeleton() {
  return (
    <div className="p-6 space-y-6 animate-pulse" role="status" aria-label="Loading webhooks">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-40 bg-slate-800/50 rounded-xl" />
        ))}
      </div>
      <div className="h-80 bg-slate-800/50 rounded-xl" />
      <span className="sr-only">Loading webhook data...</span>
    </div>
  )
}

// ── Main View ────────────────────────────────────────────────────────────────

function WebhooksViewContent() {
  const toast = useToast()
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [eventFilter, setEventFilter] = useState<Set<string>>(new Set())
  const [form, setForm] = useState({ url: '', events: new Set<string>(), secret: '' })

  const { data: endpointsData, isLoading, isError, refetch } = useQuery({
    queryKey: ['webhooks', 'endpoints'],
    queryFn: listWebhookEndpoints,
  })

  // Selected endpoint for delivery history drill-down.
  const [selectedEndpointId, setSelectedEndpointId] = useState<string | null>(null)
  const { data: deliveriesData } = useQuery({
    queryKey: ['webhooks', 'deliveries', selectedEndpointId],
    queryFn: () => webhookDeliveries(selectedEndpointId!),
    enabled: !!selectedEndpointId,
  })

  const createMut = useMutation({
    mutationFn: () => createWebhookEndpoint({
      url: form.url,
      events: Array.from(form.events),
      secret: form.secret || undefined,
    }),
    onSuccess: () => {
      toast.success('Webhook endpoint created')
      void qc.invalidateQueries({ queryKey: ['webhooks'] })
      setShowCreate(false)
      setForm({ url: '', events: new Set(), secret: '' })
    },
    onError: () => toast.error('Failed to create webhook endpoint'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteWebhookEndpoint(id),
    onSuccess: () => {
      toast.success('Webhook endpoint deleted')
      void qc.invalidateQueries({ queryKey: ['webhooks'] })
    },
    onError: () => toast.error('Failed to delete webhook endpoint'),
  })

  const pingMut = useMutation({
    mutationFn: (id: string) => pingWebhookEndpoint(id),
    onSuccess: (data) => {
      toast.success(`Ping successful: ${data.response_code} in ${data.duration_ms}ms`)
    },
    onError: () => {
      toast.error('Ping failed — endpoint did not respond')
    },
  })

  const toggleFormEvent = useCallback((key: string) => {
    setForm(f => {
      const next = new Set(f.events)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return { ...f, events: next }
    })
  }, [])

  const toggleFilter = useCallback((key: string) => {
    setEventFilter(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  // Deliveries use event_type field from backend.
  const filteredDeliveries = (deliveriesData?.deliveries ?? []).filter(
    (d: WebhookDeliveryItem) => eventFilter.size === 0 || eventFilter.has(d.event_type)
  )

  if (isLoading) return <WebhooksSkeleton />

  if (isError) {
    return (
      <div className="p-6">
        <Card>
          <EmptyState
            icon={<AlertCircle className="w-12 h-12 text-red-400" />}
            title="Failed to load webhooks"
            description="Could not fetch webhook configuration. Please try again."
            action={
              <button
                onClick={() => void refetch()}
                className="bg-brand-600 hover:bg-brand-700 text-white text-sm px-4 py-2 rounded-lg"
              >
                Retry
              </button>
            }
          />
        </Card>
      </div>
    )
  }

  const endpoints = endpointsData?.endpoints ?? []

  return (
    <div>
      <PageHeader
        title="Webhooks"
        subtitle="Manage webhook endpoints and event subscriptions"
        action={
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm px-3 py-1.5 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" /> New Endpoint
          </button>
        }
      />

      <div className={`${layout.pagePadding} ${layout.pageStack}`}>
        {/* Endpoint Cards */}
        {!endpoints.length ? (
          <Card>
            <EmptyState
              icon={<Webhook className="w-12 h-12" />}
              title="No webhook endpoints"
              description="Create your first webhook endpoint to receive real-time event notifications."
              action={
                <button
                  onClick={() => setShowCreate(true)}
                  className="bg-brand-600 hover:bg-brand-700 text-white text-sm px-4 py-2 rounded-lg"
                >
                  Create Endpoint
                </button>
              }
            />
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {(endpoints as WebhookEndpointItem[]).map(ep => {
              const health = deriveHealth(ep.failure_count)
              const healthCfg = HEALTH_CONFIG[health]
              const isSelected = selectedEndpointId === ep.id
              return (
                <Card key={ep.id} className="relative">
                  {/* Health indicator dot */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className={clsx('w-2.5 h-2.5 rounded-full shrink-0', healthCfg.bgColor)}
                        title={healthCfg.label}
                        aria-label={`Health: ${healthCfg.label}`}
                      />
                      <span className="text-sm font-medium text-slate-200 truncate" title={ep.url}>
                        {ep.url}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      <Badge color={ep.active ? 'green' : 'yellow'}>
                        {ep.active ? 'active' : 'paused'}
                      </Badge>
                      <Badge color={healthCfg.badgeColor}>
                        {healthCfg.label}
                      </Badge>
                    </div>
                  </div>

                  {/* Events */}
                  <div className="mb-3">
                    <div className="text-xs text-slate-500 mb-1">Subscribed events</div>
                    <div className="flex flex-wrap gap-1">
                      {ep.events.map(ev => (
                        <span
                          key={ev}
                          className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded"
                        >
                          {ev}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                    <div className="bg-slate-800/60 rounded-lg p-2">
                      <div className="text-slate-500 mb-0.5">Failures</div>
                      <div className={clsx(
                        'font-semibold',
                        ep.failure_count === 0 ? 'text-green-400' :
                        ep.failure_count < 10 ? 'text-yellow-400' : 'text-red-400',
                      )}>
                        {ep.failure_count}
                      </div>
                    </div>
                    <div className="bg-slate-800/60 rounded-lg p-2">
                      <div className="text-slate-500 mb-0.5">Last Delivery</div>
                      <div className="text-slate-200 font-semibold">
                        {ep.last_delivery
                          ? new Date(ep.last_delivery).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })
                          : 'Never'}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-2 border-t border-surface-border">
                    <button
                      onClick={() => pingMut.mutate(ep.id)}
                      disabled={pingMut.isPending}
                      className="flex items-center gap-1 text-xs bg-slate-700 hover:bg-slate-600 px-2.5 py-1.5 rounded-md transition-colors"
                      aria-label={`Test ping ${ep.url}`}
                    >
                      <Send className="w-3 h-3" />
                      {pingMut.isPending ? 'Pinging...' : 'Test Ping'}
                    </button>
                    <button
                      onClick={() => setSelectedEndpointId(isSelected ? null : ep.id)}
                      className={clsx(
                        'flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md transition-colors',
                        isSelected ? 'bg-brand-700 text-brand-100' : 'bg-slate-700 hover:bg-slate-600',
                      )}
                      aria-label="View delivery history"
                    >
                      <Clock className="w-3 h-3" />
                      History
                    </button>
                    <button
                      onClick={() => deleteMut.mutate(ep.id)}
                      disabled={deleteMut.isPending}
                      className="flex items-center gap-1 text-xs bg-red-900/40 hover:bg-red-900/60 text-red-300 px-2.5 py-1.5 rounded-md transition-colors ml-auto"
                      aria-label={`Delete endpoint ${ep.url}`}
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </button>
                  </div>
                </Card>
              )
            })}
          </div>
        )}

        {/* Event Filter */}
        <Card title="Event Filter">
          <div className="flex flex-wrap gap-2">
            {(() => {
              const groups = new Map<string, typeof EVENT_TYPES>()
              EVENT_TYPES.forEach(e => {
                const g = groups.get(e.group) ?? []
                g.push(e)
                groups.set(e.group, g)
              })
              return Array.from(groups.entries()).map(([group, events]) => (
                <div key={group} className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs text-slate-500 font-medium mr-1">{group}:</span>
                  {events.map(e => (
                    <label
                      key={e.key}
                      className={clsx(
                        'inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full cursor-pointer transition-colors border',
                        eventFilter.has(e.key)
                          ? 'bg-brand-700/40 text-brand-200 border-brand-600'
                          : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600',
                      )}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={eventFilter.has(e.key)}
                        onChange={() => toggleFilter(e.key)}
                        aria-label={`Filter by ${e.label}`}
                      />
                      {e.label}
                    </label>
                  ))}
                </div>
              ))
            })()}
            {eventFilter.size > 0 && (
              <button
                onClick={() => setEventFilter(new Set())}
                className="text-xs text-slate-500 hover:text-slate-300 underline"
              >
                Clear filters
              </button>
            )}
          </div>
        </Card>

        {/* Delivery History */}
        <Card title={selectedEndpointId ? `Delivery History (${filteredDeliveries.length})` : 'Delivery History'}>
          {!selectedEndpointId ? (
            <p className="text-sm text-slate-500 py-4 text-center">
              Click the <strong className="text-slate-400">History</strong> button on an endpoint card to view its delivery log.
            </p>
          ) : !filteredDeliveries.length ? (
            <p className="text-sm text-slate-500 py-4 text-center">
              No deliveries {eventFilter.size > 0 ? 'match your filter' : 'recorded for this endpoint yet'}.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" role="table" aria-label="Webhook delivery history">
                <thead>
                  <tr className="text-xs text-slate-500 border-b border-surface-border">
                    <th className="text-left pb-2 pr-4" scope="col">Status</th>
                    <th className="text-left pb-2 pr-4" scope="col">Event</th>
                    <th className="text-right pb-2 pr-4" scope="col">Response</th>
                    <th className="text-right pb-2 pr-4" scope="col">Attempts</th>
                    <th className="text-right pb-2" scope="col">Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDeliveries.slice(0, 20).map((d: WebhookDeliveryItem) => {
                    const isSuccess = d.status === 'delivered'
                    const isFailed = d.status === 'failed' || d.status === 'dead_letter'
                    const statusIcon = isSuccess
                      ? <CheckCircle className="w-3.5 h-3.5 text-green-400" aria-label="Success" />
                      : isFailed
                        ? <AlertCircle className="w-3.5 h-3.5 text-red-400" aria-label="Failed" />
                        : <Clock className="w-3.5 h-3.5 text-yellow-400" aria-label="Pending" />
                    return (
                      <tr
                        key={d.id}
                        className="border-b border-surface-border/50 hover:bg-slate-800/30"
                      >
                        <td className="py-2 pr-4">
                          <div className="flex items-center gap-1.5">
                            {statusIcon}
                            <span className={clsx(
                              'text-xs capitalize',
                              isSuccess ? 'text-green-400' :
                              isFailed ? 'text-red-400' : 'text-yellow-400',
                            )}>
                              {d.status}
                            </span>
                          </div>
                        </td>
                        <td className="py-2 pr-4">
                          <code className="text-xs text-slate-300 bg-slate-800 px-1.5 py-0.5 rounded">
                            {d.event_type}
                          </code>
                        </td>
                        <td className="py-2 pr-4 text-right">
                          {d.response_status ? (
                            <span className={clsx(
                              'text-xs font-mono',
                              d.response_status < 300 ? 'text-green-400' :
                              d.response_status < 400 ? 'text-yellow-400' : 'text-red-400',
                            )}>
                              {d.response_status}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-600">--</span>
                          )}
                        </td>
                        <td className="py-2 pr-4 text-right text-xs text-slate-400">
                          {d.attempts}
                        </td>
                        <td className="py-2 text-right text-xs text-slate-500">
                          <time dateTime={d.created_at}>
                            {new Date(d.created_at).toLocaleString('en', {
                              month: 'short', day: 'numeric',
                              hour: '2-digit', minute: '2-digit',
                            })}
                          </time>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Create Endpoint Modal */}
      {showCreate && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={() => setShowCreate(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Create new webhook endpoint"
        >
          <div
            className="bg-surface-card border border-surface-border rounded-2xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">New Webhook Endpoint</h2>
              <button
                onClick={() => setShowCreate(false)}
                className="p-1 rounded hover:bg-slate-700 text-slate-400"
                aria-label="Close dialog"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* URL */}
              <div>
                <label htmlFor="webhook-url" className="text-xs text-slate-400 mb-1 block">
                  Endpoint URL
                </label>
                <input
                  id="webhook-url"
                  type="url"
                  value={form.url}
                  onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
                  placeholder="https://example.com/webhooks/socialscale"
                  className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm"
                  required
                />
              </div>

              {/* Secret */}
              <div>
                <label htmlFor="webhook-secret" className="text-xs text-slate-400 mb-1 block">
                  Signing Secret (optional)
                </label>
                <input
                  id="webhook-secret"
                  type="text"
                  value={form.secret}
                  onChange={e => setForm(f => ({ ...f, secret: e.target.value }))}
                  placeholder="whsec_..."
                  className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm font-mono"
                />
                <p className="text-[10px] text-slate-600 mt-1">
                  Used to sign payloads for verification. Auto-generated if left empty.
                </p>
              </div>

              {/* Events */}
              <div>
                <div className="text-xs text-slate-400 mb-2">Subscribe to events</div>
                <div className="space-y-3 max-h-48 overflow-y-auto pr-1">
                  {(() => {
                    const groups = new Map<string, typeof EVENT_TYPES>()
                    EVENT_TYPES.forEach(e => {
                      const g = groups.get(e.group) ?? []
                      g.push(e)
                      groups.set(e.group, g)
                    })
                    return Array.from(groups.entries()).map(([group, events]) => (
                      <div key={group}>
                        <div className="text-xs text-slate-500 font-medium mb-1">{group}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {events.map(e => (
                            <label
                              key={e.key}
                              className={clsx(
                                'inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded cursor-pointer transition-colors border',
                                form.events.has(e.key)
                                  ? 'bg-brand-700/40 text-brand-200 border-brand-600'
                                  : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600',
                              )}
                            >
                              <input
                                type="checkbox"
                                className="sr-only"
                                checked={form.events.has(e.key)}
                                onChange={() => toggleFormEvent(e.key)}
                              />
                              {e.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))
                  })()}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowCreate(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-sm py-2 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() => createMut.mutate()}
                  disabled={createMut.isPending || !form.url || form.events.size === 0}
                  className="flex-1 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm py-2 rounded-lg"
                >
                  {createMut.isPending ? 'Creating...' : 'Create Endpoint'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default function WebhooksView() {
  return (
    <ErrorBoundary>
      <WebhooksViewContent />
    </ErrorBoundary>
  )
}
